/* Member card presentation for the Manager org-settings grid (#342).
 *
 * Pure presentation: no store, no fetch, and deliberately NO export imports.
 * The per-member export buttons arrive as opaque optional props so the export
 * workflows can stay on the other side of the boundary (Patricia's #343).
 * Moved verbatim out of index.tsx at 859565e3. */
import {
  Trash2, KeyRound, Loader2, Check,
  Pencil, X, ShieldCheck, Users as UsersIcon, TrendingUp, Bot, Zap, Layers, FlaskConical,
  ToggleLeft, ToggleRight, ExternalLink, Settings as SettingsIcon, Download,
  Receipt, ClipboardList, Rocket, Handshake, Radio, Crosshair, Swords,
} from 'lucide-react';
import { computeHeadroom, formatHeadroomDisplay } from '../../lib/limits';
import type { TraderRules } from '../../types/shared/admin';
import type { OrgMember, MemberUsage } from './memberTypes';

export type LabAccessChoice = 'off' | 'viewer' | 'researcher';

const LAB_ACCESS_LABELS: Record<LabAccessChoice, string> = {
  off: 'Lab off',
  viewer: 'Lab viewer',
  researcher: 'Lab researcher',
};

// ── Role Badge ──────────────────────────────────────────────────

const ROLE_STYLES: Record<string, string> = {
  admin: 'bg-purple-500/15 text-purple-600 dark:text-purple-400',
  manager: 'bg-blue-500/15 text-blue-600 dark:text-blue-400',
  trader: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400',
};

const ROLE_ICONS: Record<string, typeof ShieldCheck> = {
  admin: ShieldCheck,
  manager: UsersIcon,
  trader: TrendingUp,
};

function RoleBadge({ role }: { role: string }) {
  const Icon = ROLE_ICONS[role] ?? TrendingUp;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium ${ROLE_STYLES[role] ?? ''}`}>
      <Icon size={10} />
      {role}
    </span>
  );
}

// ── Member Card ─────────────────────────────────────────────────

export interface MemberCardProps {
  member: OrgMember;
  usage?: MemberUsage;
  editing: boolean;
  editUsername: string;
  onStartEdit: () => void;
  onCancelEdit: () => void;
  onSaveEdit: () => void;
  onEditUsernameChange: (v: string) => void;
  onResetPassword?: () => void;
  onDelete?: () => void;
  /** #295 — this caller may govern a NON-trader member's account (rename it).
   *
   *  Deliberately a separate flag rather than gating the rename on `onStartEdit`
   *  being present: `onStartEdit` is required and every caller passes it, so
   *  presence carries no permission signal. Reset-password and delete keep the
   *  file's usual presence-gating because they ARE optional.
   *
   *  Defaults to false, so a caller that knows nothing about #295 renders the
   *  card exactly as it did before. */
  canGovernMember?: boolean;
  onToggleUseOrgKeys?: (next: boolean) => void;
  onToggleBotsEnabled?: (next: boolean) => void;
  /** Org-level bot switch — when false, the per-trader toggle is shown disabled
   *  (granting a trader is moot until org bots are enabled). null = unknown. */
  orgBotsEnabled?: boolean | null;
  /** Oscar overtake/blocker access (traderRules.oscarEnabled, fail-closed). */
  onToggleOscarEnabled?: (next: boolean) => void;
  /** Armed one-click access (traderRules.oneClickEnabled, fail-closed). */
  onToggleOneClickEnabled?: (next: boolean) => void;
  /** Live Trading V2 surface access (traderRules.lt2Enabled, fail-closed).
   *  Trader rows only — the size ceiling it needs to become active lives in
   *  Edit limits, which is itself trader-only. */
  onToggleLt2Enabled?: (next: boolean) => void;
  /** StreamRift Cockpit / Larry bot-management access (traderRules.cockpitEnabled, fail-closed). */
  onToggleCockpitEnabled?: (next: boolean) => void;
  /** RFQ desk access (traderRules.rfqEnabled, fail-closed). */
  onToggleRfqEnabled?: (next: boolean) => void;
  /** TapeLab level (traderRules.labAccess, fail-closed). 'off' = remove the key. */
  onSetLabAccess?: (next: LabAccessChoice) => void;
  /** Single-game live trading view access (traderRules.liveGameEnabled,
   *  fail-closed). View exposure only — order capability stays with the
   *  one-click/LT2 chips. */
  onToggleLiveGameEnabled?: (next: boolean) => void;
  /** Sniper access (traderRules.sniperEnabled, fail-closed, no manager role
   *  pass). Arms real-money runners on the ORG's keys — grant deliberately. */
  onToggleSniperEnabled?: (next: boolean) => void;
  /** Live Trading V3 Attack (W/E) access (traderRules.attackEnabled,
   *  fail-closed). Trader rows only, for the same ceiling reason. */
  onToggleAttackEnabled?: (next: boolean) => void;
  onEditLimits?: () => void;
  onExport?: () => void;
  exporting?: boolean;
  /** This trader's export error message, when its last export failed. */
  exportError?: string | null;
  /** Settlements CSV (#122) — independent of the fill/accounting export above. */
  onSettlementExport?: () => void;
  settlementExporting?: boolean;
  settlementExportError?: string | null;
  /** Order audit CSV (#133) — independent of both exports above. */
  onOrderAuditExport?: () => void;
  orderAuditExporting?: boolean;
  orderAuditExportError?: string | null;
  onViewDetail: () => void;
}

/** One capability toggle on a member card.
 *
 *  Pill-shaped so the capability region reads as a group rather than a run of
 *  loose words, and `whitespace-nowrap` so a chip wraps as a WHOLE UNIT: the
 *  #294 defect was "1-click off" folding at its hyphen into a vertical
 *  fragment once the row ran out of width. `shrink-0` keeps a chip at its
 *  natural width instead of being squeezed until the label breaks. */
function CapabilityChip({
  icon: Icon,
  iconClass,
  label,
  title,
  tinted = false,
  disabled = false,
  onToggle,
}: {
  icon: typeof ShieldCheck;
  /** Colour for the icon glyph — the per-capability signal the card already
   *  carried: blue/amber for the org-vs-personal key MODE, emerald for a
   *  granted capability, muted otherwise. */
  iconClass: string;
  /** Carries the on/off state ("Bots on" / "Bots off") and, because it is the
   *  chip's only text, IS its accessible name. Deliberately no aria-label: one
   *  would override the visible label and break WCAG 2.5.3 (a speech-input user
   *  saying "1-click off" must be able to activate what they can read). `title`
   *  stays the tooltip/description, exactly as it was before #294. */
  label: string;
  title: string;
  /** Tint the pill itself. Grants tint when on; the key-mode chip never does —
   *  using org keys is the default, not a privilege. */
  tinted?: boolean;
  disabled?: boolean;
  onToggle: () => void;
}) {
  return (
    // No aria-pressed: the label already changes with state, and a toggle whose
    // name changes should not also claim a pressed state (ARIA APG). It read
    // worst on the key-mode chip, where "not pressed" implied an off state that
    // does not exist — org vs personal keys is a mode, not a grant.
    <button
      type="button"
      onClick={() => { if (!disabled) onToggle(); }}
      disabled={disabled}
      title={title}
      className={`inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full border px-2 py-0.5 text-[11px] transition-colors ${
        disabled
          ? 'border-border/60 text-muted-foreground/50 cursor-not-allowed'
          : tinted
            // Bare emerald-400: globals.css remaps `.light .text-emerald-400` to
            // --positive (~emerald-700). Tailwind's `dark:` is INERT here — the
            // app toggles a `.light` class, not `.dark` (tailwind.config.js).
            ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400 hover:border-emerald-500/50'
            : 'border-border text-muted-foreground hover:border-muted-foreground/40 hover:text-foreground'
      }`}
    >
      <Icon size={12} className={iconClass} />
      {label}
    </button>
  );
}

export function MemberCard(props: MemberCardProps) {
  const {
    member, usage, editing, editUsername,
    onStartEdit, onCancelEdit, onSaveEdit, onEditUsernameChange,
    onResetPassword, onDelete, canGovernMember = false, onToggleUseOrgKeys, onToggleBotsEnabled, orgBotsEnabled, onToggleOscarEnabled, onToggleOneClickEnabled, onToggleLt2Enabled, onToggleCockpitEnabled, onToggleRfqEnabled, onSetLabAccess, onToggleLiveGameEnabled, onToggleSniperEnabled, onToggleAttackEnabled, onEditLimits, onExport, exporting, exportError,
    onSettlementExport, settlementExporting, settlementExportError,
    onOrderAuditExport, orderAuditExporting, orderAuditExportError,
    onViewDetail,
  } = props;
  const isTrader = member.role === 'trader';
  const useOrgKeys = member.useOrgKeys ?? true;
  const botsEnabled = member.botsEnabled ?? false;
  // Fail-closed like the relay: missing key = no oscar access.
  const oscarEnabled = member.traderRules?.oscarEnabled === true;
  const oneClickEnabled = member.traderRules?.oneClickEnabled === true;
  const lt2Enabled = member.traderRules?.lt2Enabled === true;
  const cockpitEnabled = member.traderRules?.cockpitEnabled === true;
  const rfqEnabled = member.traderRules?.rfqEnabled === true;
  const liveGameEnabled = member.traderRules?.liveGameEnabled === true;
  const sniperEnabled = member.traderRules?.sniperEnabled === true;
  // The relay refuses an LT2 order unless ALL THREE are present, in this order:
  // lt2Enabled (LT2_NOT_ENABLED) → oneClickEnabled (ONE_CLICK_NOT_ENABLED) →
  // an explicit ceiling (LT2_SIZE_LIMIT_REQUIRED), all before venue I/O
  // (relay/src/routes/kalshi-proxy.ts). So the grant alone can be live and
  // still buy the trader nothing. Naming that on the granting surface follows
  // the bots chip, which has said "org off" since #294 for the same reason —
  // a chip that reads plainly "on" while granting nothing is the defect.
  const lt2Missing: string[] = [];
  if (!oneClickEnabled) lt2Missing.push('armed one-click access');
  if (member.traderRules?.oneClickMaxContracts == null) {
    lt2Missing.push('an explicit one-click size ceiling (Edit limits)');
  }
  const lt2Inactive = lt2Enabled && lt2Missing.length > 0;
  // Fail-closed like the relay: anything but the two levels reads as off.
  const labAccess: LabAccessChoice =
    member.traderRules?.labAccess === 'viewer' || member.traderRules?.labAccess === 'researcher'
      ? member.traderRules.labAccess
      : 'off';
  // Attack (V3) layers on the full LT2 stack AND its own explicit ceiling —
  // the relay refuses ATTACK_NOT_ENABLED → ATTACK_SIZE_LIMIT_REQUIRED after
  // the LT2 gates, so the chip names every missing precondition the same way
  // the LT2 chip does.
  const attackEnabled = member.traderRules?.attackEnabled === true;
  const attackMissing: string[] = [...lt2Missing];
  if (!lt2Enabled) attackMissing.unshift('Live Trading V2 access');
  if (member.traderRules?.attackMaxContracts == null) {
    attackMissing.push('an explicit attack size ceiling (Edit limits)');
  }
  const attackInactive = attackEnabled && attackMissing.length > 0;
  // Effective bot access is org AND user; when the org switch is off the
  // per-trader grant is inert — show the toggle disabled + labeled so it can't
  // claim "on" while granting nothing. null = not yet known (don't gate).
  const orgBotsOff = orgBotsEnabled === false;
  // The capability region is omitted outright when a role exposes no toggles
  // (a manager viewed by a manager) rather than rendering an empty bordered
  // strip — that dead space was part of the #294 complaint.
  const hasCapabilities = Boolean(
    onToggleUseOrgKeys || onToggleBotsEnabled || onToggleOscarEnabled || onToggleOneClickEnabled
      || onToggleLt2Enabled || onToggleCockpitEnabled || onToggleRfqEnabled || onSetLabAccess
      || onToggleLt2Enabled || onToggleCockpitEnabled || onToggleRfqEnabled || onSetLabAccess
      || onToggleLiveGameEnabled || onToggleSniperEnabled || onToggleAttackEnabled,
  );
  const iconTone = (on: boolean) => (on ? 'text-emerald-500' : 'text-muted-foreground');

  return (
    // overflow-hidden is a structural backstop: no control can escape the card
    // even if a label grows later. The regions below are what actually keeps
    // the layout contained.
    <div className="bg-card border-border flex flex-col overflow-hidden rounded-lg border">
      <div className="p-3">
        <div className="mb-1.5 flex items-start justify-between gap-2">
          {editing ? (
            <input value={editUsername} onChange={(e) => onEditUsernameChange(e.target.value)}
              aria-label="Username"
              className="bg-background border-border text-foreground min-w-0 flex-1 rounded border px-2 py-0.5 text-sm font-medium focus:border-blue-500 focus:outline-none" />
          ) : (
            // min-w-0 + truncate: an unbroken username (no space to break at)
            // otherwise sets a min-content floor wider than the grid track and
            // pushes the role badge out of the card. Tooltip keeps it readable.
            <span className="text-foreground min-w-0 flex-1 truncate text-sm font-medium" title={member.username}>
              {member.username}
            </span>
          )}
          <span className="shrink-0">
            <RoleBadge role={member.role} />
          </span>
        </div>
        <p className="text-muted-foreground text-[11px]">Joined {new Date(member.createdAt).toLocaleDateString()}</p>
      </div>

      {hasCapabilities && (
        <div
          role="group"
          aria-label="Capabilities"
          className="border-border flex flex-wrap items-center gap-1.5 border-t px-3 py-2"
        >
          {onToggleUseOrgKeys ? (
            <CapabilityChip
              icon={useOrgKeys ? ToggleRight : ToggleLeft}
              iconClass={useOrgKeys ? 'text-blue-500' : 'text-amber-500'}
              label={useOrgKeys ? 'Org keys' : 'Personal keys'}
              title={useOrgKeys ? 'Uses org keys' : 'Uses personal keys'}
              onToggle={() => onToggleUseOrgKeys(!useOrgKeys)}
            />
          ) : null}
          {onToggleBotsEnabled ? (
            <CapabilityChip
              icon={Bot}
              iconClass={orgBotsOff ? 'text-muted-foreground/50' : iconTone(botsEnabled)}
              label={orgBotsOff ? (botsEnabled ? 'Bots on · org off' : 'Bots off') : botsEnabled ? 'Bots on' : 'Bots off'}
              title={
                orgBotsOff
                  ? 'Org bots are disabled — enable org bots first; this grant has no effect until then'
                  : botsEnabled
                    ? 'Bot access on — click to revoke'
                    : 'Bot access off — click to grant'
              }
              tinted={botsEnabled && !orgBotsOff}
              disabled={orgBotsOff}
              onToggle={() => onToggleBotsEnabled(!botsEnabled)}
            />
          ) : null}
          {onToggleOscarEnabled ? (
            <CapabilityChip
              icon={ShieldCheck}
              iconClass={iconTone(oscarEnabled)}
              label={oscarEnabled ? 'Oscar on' : 'Oscar off'}
              title={
                oscarEnabled
                  ? 'Oscar overtake/blocker access on — click to revoke'
                  : 'Oscar overtake/blocker access off — click to grant'
              }
              tinted={oscarEnabled}
              onToggle={() => onToggleOscarEnabled(!oscarEnabled)}
            />
          ) : null}
          {onToggleCockpitEnabled ? (
            <CapabilityChip
              icon={Rocket}
              iconClass={iconTone(cockpitEnabled)}
              label={cockpitEnabled ? 'Cockpit on' : 'Cockpit off'}
              title={
                cockpitEnabled
                  ? 'StreamRift Cockpit / Larry bot-management access on — click to revoke'
                  : 'StreamRift Cockpit / Larry bot-management access off — click to grant'
              }
              tinted={cockpitEnabled}
              onToggle={() => onToggleCockpitEnabled(!cockpitEnabled)}
            />
          ) : null}
          {onToggleRfqEnabled ? (
            <CapabilityChip
              icon={Handshake}
              iconClass={iconTone(rfqEnabled)}
              label={rfqEnabled ? 'RFQ on' : 'RFQ off'}
              title={
                rfqEnabled
                  ? 'RFQ desk access on — click to revoke'
                  : 'RFQ desk access off — click to grant'
              }
              tinted={rfqEnabled}
              onToggle={() => onToggleRfqEnabled(!rfqEnabled)}
            />
          ) : null}
          {onToggleLiveGameEnabled ? (
            <CapabilityChip
              icon={Radio}
              iconClass={iconTone(liveGameEnabled)}
              label={liveGameEnabled ? 'Live game on' : 'Live game off'}
              title={
                liveGameEnabled
                  ? 'Single-game live trading view access on — click to revoke'
                  : 'Single-game live trading view access off — click to grant'
              }
              tinted={liveGameEnabled}
              onToggle={() => onToggleLiveGameEnabled(!liveGameEnabled)}
            />
          ) : null}
          {onToggleSniperEnabled ? (
            <CapabilityChip
              icon={Crosshair}
              iconClass={iconTone(sniperEnabled)}
              label={sniperEnabled ? 'Sniper on' : 'Sniper off'}
              title={
                sniperEnabled
                  ? 'Sniper access on (runs on ORG keys) — click to revoke'
                  : 'Sniper access off — click to grant (arms real-money runners on ORG keys)'
              }
              tinted={sniperEnabled}
              onToggle={() => onToggleSniperEnabled(!sniperEnabled)}
            />
          ) : null}
          {onToggleOneClickEnabled ? (
            <CapabilityChip
              icon={Zap}
              iconClass={iconTone(oneClickEnabled)}
              label={oneClickEnabled ? '1-click on' : '1-click off'}
              title={
                oneClickEnabled
                  ? 'Armed one-click access on — click to revoke'
                  : 'Armed one-click access off — click to grant'
              }
              tinted={oneClickEnabled}
              onToggle={() => onToggleOneClickEnabled(!oneClickEnabled)}
            />
          ) : null}
          {onToggleLt2Enabled ? (
            // Spelled out rather than "LT2": the capability is days old, and
            // unlike "Oscar" the acronym names nothing the reader has seen
            // elsewhere in the product. The chip is whitespace-nowrap +
            // shrink-0 like its siblings, so the longer label wraps as a WHOLE
            // UNIT to the next row of the flex-wrap region instead of folding
            // mid-label (#294).
            <CapabilityChip
              icon={Layers}
              iconClass={lt2Inactive ? 'text-amber-500' : iconTone(lt2Enabled)}
              label={
                lt2Enabled
                  ? lt2Inactive
                    ? 'Live Trading V2 on · inactive'
                    : 'Live Trading V2 on'
                  : 'Live Trading V2 off'
              }
              title={
                lt2Inactive
                  ? `Live Trading V2 is granted but inactive — the trader also needs ${lt2Missing.join(' and ')}. Click to revoke.`
                  : lt2Enabled
                    ? 'Live Trading V2 surfaces on — click to revoke. Revoking stops new V2 actions once the trader next refreshes their session; it does not cancel orders already placed.'
                    : 'Live Trading V2 surfaces off — click to grant. A trader with no setting is off.'
              }
              tinted={lt2Enabled && !lt2Inactive}
              onToggle={() => onToggleLt2Enabled(!lt2Enabled)}
            />
          ) : null}
          {onSetLabAccess ? (
            // A select, not a cycling chip: three states behind one click is
            // too easy to overshoot, and "researcher" lets a trader launch
            // studies in the lab — a deliberate choice, not a toggle.
            <label
              className={`inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full border px-2 py-0.5 text-[11px] transition-colors ${
                labAccess !== 'off'
                  ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400 hover:border-emerald-500/50'
                  : 'border-border text-muted-foreground hover:border-muted-foreground/40 hover:text-foreground'
              }`}
              title="TapeLab access: off, viewer (look only) or researcher (can run studies)"
            >
              <FlaskConical size={12} className={iconTone(labAccess !== 'off')} />
              <select
                aria-label={`Lab access for ${member.username}`}
                value={labAccess}
                onChange={(e) => onSetLabAccess(e.target.value as LabAccessChoice)}
                className="cursor-pointer appearance-none bg-transparent pr-3 text-[11px] text-inherit outline-none"
              >
                {(Object.keys(LAB_ACCESS_LABELS) as LabAccessChoice[]).map((k) => (
                  <option key={k} value={k}>{LAB_ACCESS_LABELS[k]}</option>
                ))}
              </select>
            </label>
          ) : null}
          {/* #741 — this plane CANNOT know a trader's keys: the mapping is
              browser-local and per user (src/stores/liveHotkeys.ts) and no
              relay route or TraderRules field carries it. So W/E is named as
              the shipped DEFAULT, never as a statement of the trader's current
              keys — the house wording from docs/live-trading-v3-attack-pull-
              baseline.md ("Key bindings are defaults"). Do not re-inline the
              keys into the capability name. */}
          {onToggleAttackEnabled ? (
            <CapabilityChip
              icon={Swords}
              iconClass={attackInactive ? 'text-amber-500' : iconTone(attackEnabled)}
              label={
                attackEnabled
                  ? attackInactive
                    ? 'Attack on · inactive'
                    : 'Attack on'
                  : 'Attack off'
              }
              title={
                attackInactive
                  ? `Attack is granted but inactive — the trader also needs ${attackMissing.join(' and ')}. Click to revoke.`
                  : attackEnabled
                    ? 'Attack hotkeys on — bounded aggressive orders that may intentionally take liquidity. W/E are the shipped defaults; key mapping is per trader, lives in their browser, and is not visible here. Click to revoke; resting orders are untouched.'
                    : 'Attack hotkeys off — click to grant. A trader with no setting is off. W/E are the shipped defaults; key mapping is per trader, lives in their browser, and is not visible here.'
              }
              tinted={attackEnabled && !attackInactive}
              onToggle={() => onToggleAttackEnabled(!attackEnabled)}
            />
          ) : null}
        </div>
      )}

      {isTrader && (
        <LimitsRow
          maxContracts={member.maxContracts}
          maxPositions={member.maxPositions}
          traderRules={member.traderRules}
          usage={usage}
          onEdit={onEditLimits}
        />
      )}

      {/* Secondary actions get their OWN region rather than sharing a row with
          the capability toggles. Sharing was the #294 escape: the rail had no
          shrink-0 and no width of its own, so on a cramped track its icons ran
          past the card's right edge. `title` strings are load-bearing — the
          manager export tests select these buttons by title. */}
      <div
        role="group"
        aria-label="Member actions"
        className="border-border flex flex-wrap items-center justify-end gap-0.5 border-t px-3 py-1.5"
      >
        {editing ? (
          <>
            <button type="button" onClick={onSaveEdit} title="Save username" aria-label={`Save username for ${member.username}`} className="shrink-0 rounded p-1 text-green-500 hover:bg-green-500/10"><Check size={14} /></button>
            <button type="button" onClick={onCancelEdit} title="Cancel edit" aria-label="Cancel edit" className="text-muted-foreground shrink-0 rounded p-1 hover:bg-red-500/10 hover:text-red-400"><X size={14} /></button>
          </>
        ) : (
          <>
            {(isTrader || canGovernMember) && (
              <button type="button" onClick={onStartEdit} title="Edit" aria-label={`Edit ${member.username}`} className="text-muted-foreground shrink-0 rounded p-1 hover:bg-blue-500/10 hover:text-blue-400"><Pencil size={12} /></button>
            )}
            {onResetPassword && (
              <button type="button" onClick={onResetPassword} title="Reset password" aria-label={`Reset password for ${member.username}`} className="text-muted-foreground shrink-0 rounded p-1 hover:bg-yellow-500/10 hover:text-yellow-400"><KeyRound size={12} /></button>
            )}
            {onExport && (
              <button
                type="button"
                onClick={onExport}
                disabled={exporting}
                title={exportError ? `Retry export — ${exportError}` : 'Export fill/accounting CSV'}
                aria-label={`Export fill/accounting CSV for ${member.username}`}
                className={`shrink-0 rounded p-1 hover:bg-emerald-500/10 hover:text-emerald-400 disabled:opacity-50 ${
                  exportError ? 'text-red-400' : 'text-muted-foreground'
                }`}
              >
                {exporting ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
              </button>
            )}
            {onSettlementExport && (
              <button
                type="button"
                onClick={onSettlementExport}
                disabled={settlementExporting}
                title={
                  settlementExportError
                    ? `Retry Settlements CSV — ${settlementExportError}`
                    : 'Export Settlements CSV'
                }
                aria-label={`Export Settlements CSV for ${member.username}`}
                className={`shrink-0 rounded p-1 hover:bg-emerald-500/10 hover:text-emerald-400 disabled:opacity-50 ${
                  settlementExportError ? 'text-red-400' : 'text-muted-foreground'
                }`}
              >
                {settlementExporting ? <Loader2 size={12} className="animate-spin" /> : <Receipt size={12} />}
              </button>
            )}
            {onOrderAuditExport && (
              <button
                type="button"
                onClick={onOrderAuditExport}
                disabled={orderAuditExporting}
                title={
                  orderAuditExportError
                    ? `Retry Order audit CSV — ${orderAuditExportError}`
                    : 'Export Order audit CSV'
                }
                aria-label={`Export Order audit CSV for ${member.username}`}
                className={`shrink-0 rounded p-1 hover:bg-emerald-500/10 hover:text-emerald-400 disabled:opacity-50 ${
                  orderAuditExportError ? 'text-red-400' : 'text-muted-foreground'
                }`}
              >
                {orderAuditExporting ? <Loader2 size={12} className="animate-spin" /> : <ClipboardList size={12} />}
              </button>
            )}
            {onDelete && (
              <button type="button" onClick={onDelete} title="Delete" aria-label={`Delete ${member.username}`} className="text-muted-foreground shrink-0 rounded p-1 hover:bg-red-500/10 hover:text-red-400"><Trash2 size={12} /></button>
            )}
          </>
        )}
      </div>

      {/* Each export reports its own failure: with several download buttons on
          the card, an unqualified "Export failed" no longer says which one. */}
      {(exportError || settlementExportError || orderAuditExportError) && (
        <div
          aria-live="polite"
          className="border-border border-t px-3 py-1.5 text-[11px] text-red-400"
        >
          {exportError && (
            <p>Fill/accounting export failed: {exportError} — click the download icon to retry.</p>
          )}
          {settlementExportError && (
            <p>Settlements export failed: {settlementExportError} — click the receipt icon to retry.</p>
          )}
          {orderAuditExportError && (
            <p>Order audit export failed: {orderAuditExportError} — click the clipboard icon to retry.</p>
          )}
        </div>
      )}

      <button
        type="button"
        onClick={onViewDetail}
        className="border-border text-muted-foreground hover:bg-muted/30 hover:text-foreground flex items-center justify-center gap-1.5 border-t px-3 py-2 text-[11px]"
      >
        <ExternalLink size={11} /> View activity
      </button>
    </div>
  );
}

// ── Limits Row (per-card fill bars + edit shortcut) ─────────────

interface LimitsRowProps {
  maxContracts: number | null;
  maxPositions: number | null;
  traderRules: TraderRules | null;
  usage?: MemberUsage;
  onEdit?: () => void;
}

/** Compact one-line summary of trader_rules for the MemberCard footer.
 *  Returns null when nothing is restricted (so we don't render an
 *  uninformative "All allowed" line). */
function summarizeTraderRules(rules: TraderRules | null): string | null {
  if (!rules) return null;
  const parts: string[] = [];
  if (rules.allowedSports !== null && rules.allowedSports.length > 0) {
    parts.push(`${rules.allowedSports.join(', ')} only`);
  }
  if (!rules.nonSportsEnabled) parts.push('no non-sports');
  if (!rules.polymarketEnabled) parts.push('no new Poly');
  return parts.length > 0 ? parts.join(' · ') : null;
}

const STATUS_BAR_COLOURS: Record<string, string> = {
  green: 'bg-emerald-500',
  yellow: 'bg-amber-500',
  red: 'bg-red-500',
  over: 'bg-red-600',
  unlimited: 'bg-blue-400',
};

function LimitsRow({ maxContracts, maxPositions, traderRules, usage, onEdit }: LimitsRowProps) {
  // Resting buy intent counts toward the contracts denominator so the bar
  // matches what the relay's checkOrderLimit will actually see.
  const contractsCurrent = usage
    ? usage.openContracts + usage.intentContracts
    : 0;
  const positionsCurrent = usage?.openPositions ?? 0;
  const cHead = computeHeadroom(contractsCurrent, maxContracts);
  const pHead = computeHeadroom(positionsCurrent, maxPositions);
  const rulesSummary = summarizeTraderRules(traderRules);

  return (
    <div className="border-border space-y-1.5 border-t px-3 py-2">
      {/* One grid for BOTH bars, not a flex row each (#492). The readout column
          is content-sized, so it fits the WIDEST value across the two rows and
          both rows then share that width — which is what keeps the fill bars
          aligned with each other.

          The column it replaces was a fixed `w-24` (96px) with no truncation, so
          a readout wider than that WRAPPED and grew its row: measured at all
          three desktop baselines, "1,284,905 / Unlimited" needs 104px and
          "986,420 / 1,000,000" needs 101px. Caps are unbounded (the relay's
          parseLimitField takes any non-negative integer) and null renders as the
          long word "Unlimited", so no fixed width is safe — hence a content-sized
          track plus `whitespace-nowrap` on the value.

          The readout track keeps a 6rem FLOOR — the exact width it replaces —
          so it only grows for a readout that would previously have wrapped. Any
          card that rendered correctly before is therefore pixel-identical after,
          and all of those cards keep one shared bar length: the bars are
          proportion gauges read side by side across the grid, so letting every
          card size its own bar would make a 50% fill on a long bar draw longer
          than a 60% fill on a short one. A card whose readout DOES outgrow the
          floor still draws a bar ~8-10px shorter than its neighbours — the floor
          protects the cards that already fit, not the outliers.

          The bar keeps a 2rem floor so a wide readout squeezes it rather than
          erasing it. The readout track stops growing at the space that leaves
          (195px at the narrowest measured track: 331px − 24px padding − 64px
          label − 32px bar − 16px gaps), which is ~39 characters. Past that the
          text is clipped by the card's overflow-hidden with no ellipsis. That
          IS reachable — EditLimitsModal's parseLimitInputValue accepts any safe
          integer, so a 16-digit cap on both sides is ~45 characters — but it
          takes a cap no desk would set, and the alternative (truncating) would
          delete digits from every large-but-legitimate readout instead. */}
      <div className="grid grid-cols-[4rem_minmax(2rem,1fr)_minmax(6rem,auto)] items-center gap-x-2 gap-y-1.5 text-[10px]">
        <BarRow
          label="Positions"
          current={positionsCurrent}
          cap={maxPositions}
          status={pHead.status}
          fraction={pHead.fractionUsed}
        />
        <BarRow
          label="Contracts"
          current={contractsCurrent}
          cap={maxContracts}
          status={cHead.status}
          fraction={cHead.fractionUsed}
        />
      </div>
      {rulesSummary && (
        <div className="flex items-center gap-2 text-[10px] text-amber-600 dark:text-amber-400">
          <span className="w-16 shrink-0">Rules</span>
          <span className="flex-1 truncate" title={rulesSummary}>{rulesSummary}</span>
        </div>
      )}
      {onEdit && (
        <button
          onClick={onEdit}
          title="Edit limits"
          className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1 text-[10px]"
        >
          <SettingsIcon size={10} /> Edit limits
        </button>
      )}
    </div>
  );
}

function BarRow({
  label,
  current,
  cap,
  status,
  fraction,
}: {
  label: string;
  current: number;
  cap: number | null;
  status: string;
  fraction: number | null;
}) {
  const widthPct = cap == null || fraction == null
    ? 100
    : Math.max(0, Math.min(100, fraction * 100));
  const bar = STATUS_BAR_COLOURS[status] ?? 'bg-muted-foreground';
  // Three bare cells, not a row wrapper: the caller's grid owns the tracks, so
  // both bars land in the same columns and stay aligned. Widths come from the
  // grid template — a width here would defeat the shared readout track.
  return (
    <>
      <span className="text-muted-foreground">{label}</span>
      <div className="bg-muted/30 relative h-1.5 overflow-hidden rounded">
        <div className={`${bar} h-full`} style={{ width: `${widthPct}%`, opacity: cap == null ? 0.35 : 1 }} />
      </div>
      <span className="text-muted-foreground whitespace-nowrap text-right tabular-nums">
        {formatHeadroomDisplay(current, cap)}
      </span>
    </>
  );
}
